import React, { FC, useCallback, useEffect, useState } from "react";
import { useSetRecoilState } from "recoil";
import { styled } from "@mui/system";
import { toast } from "react-toastify";

import Dialog from "../Display/Dialog";
import { ButtonVariants } from "../Display/Button";
import Text, { TextVariant } from "../Display/Text";
import Loading, { LoadingVariant } from "../Display/Loading";
import {
  COLORS,
  FONT_FAMILIES,
  FONT_WEIGHTS,
  GAPS,
  RADII,
  SPACINGS,
} from "../../common/consts";
import { organizationAtom } from "../../state/atoms";
import {
  addBillingSeats,
  getBillingStatus,
  getMyOrganization,
} from "../../services/firebase";
import { BillingStatus } from "../../types/billing";

const Stepper = styled("div")`
  display: flex;
  align-items: center;
  gap: ${SPACINGS.md};
  justify-content: center;
`;

const StepButton = styled("button")`
  width: 40px;
  height: 40px;
  border-radius: ${RADII.md};
  border: 1px solid var(--color-borderPrimary, ${COLORS.borderPrimary});
  background-color: var(--color-backgroundWhite, ${COLORS.backgroundWhite});
  color: var(--color-foregroundBrand, ${COLORS.foregroundBrand});
  font-size: 1.25rem;
  font-family: ${FONT_FAMILIES.regular};
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover:not(:disabled) {
    background-color: var(--color-backgroundSecondary, ${COLORS.backgroundSecondary});
  }

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

const SeatCount = styled("div")`
  min-width: 64px;
  text-align: center;
  font-family: ${FONT_FAMILIES.titles};
  font-weight: ${FONT_WEIGHTS.semibold};
  font-size: 1.75rem;
  color: var(--color-foregroundPrimary, ${COLORS.foregroundPrimary});
`;

const SummaryBox = styled("div")`
  border: 1px solid var(--color-borderPrimary, ${COLORS.borderPrimary});
  border-radius: ${RADII.md};
  padding: ${SPACINGS.md} ${SPACINGS.lg};
  background-color: var(--color-backgroundSecondaryCard, ${COLORS.backgroundSecondaryCard});
  display: flex;
  flex-direction: column;
  gap: ${SPACINGS.xs};
`;

const formatAmount = (amountInCents: number, currency: string): string =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
    minimumFractionDigits: amountInCents % 100 === 0 ? 0 : 2,
  }).format(amountInCents / 100);

export interface AddLicensesDialogProps {
  open: boolean;
  onClose: () => void;
  /** Called after seats were added and the org atom refreshed. */
  onAdded?: () => void;
}

const AddLicensesDialog: FC<AddLicensesDialogProps> = ({
  open,
  onClose,
  onAdded,
}) => {
  const setOrganization = useSetRecoilState(organizationAtom);
  const [status, setStatus] = useState<BillingStatus | null>(null);
  const [count, setCount] = useState(1);
  const [isWorking, setIsWorking] = useState(false);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    if (!open) {
      return;
    }
    let isMounted = true;
    setCount(1);
    setLoadError("");
    setStatus(null);
    getBillingStatus()
      .then((billingStatus) => {
        if (isMounted) {
          setStatus(billingStatus);
        }
      })
      .catch((error) => {
        if (isMounted) {
          setLoadError(
            error instanceof Error ? error.message : "Could not load billing"
          );
        }
      });
    return () => {
      isMounted = false;
    };
  }, [open]);

  const billing = status?.billing;
  const interval = billing?.interval === "yearly" ? "yearly" : "monthly";
  const price =
    interval === "yearly"
      ? status?.config.prices?.yearly
      : status?.config.prices?.monthly;

  const handleConfirm = useCallback(async () => {
    setIsWorking(true);
    try {
      const result = await addBillingSeats(count);
      const { result: organization } = await getMyOrganization();
      if (organization) {
        setOrganization(organization);
      }
      toast.success(
        `Added ${count} license${count === 1 ? "" : "s"} — you now have ${result.availableLicenses}`
      );
      onClose();
      onAdded?.();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Could not add licenses"
      );
    } finally {
      setIsWorking(false);
    }
  }, [count, onAdded, onClose, setOrganization]);

  const renderBody = () => {
    if (loadError) {
      return <Text variant={TextVariant.sm}>{loadError}</Text>;
    }
    if (!status) {
      return (
        <div
          className="d-flex align-items-center justify-content-center"
          style={{ minHeight: 140 }}
        >
          <Loading variant={LoadingVariant.CIRCULAR} />
        </div>
      );
    }

    const currentPaid = billing?.seats || 0;
    const freeSeats = status.freeSeats || 0;
    const deltaLabel = price?.unitAmount
      ? `${formatAmount(price.unitAmount * count, price.currency)} / ${
          interval === "yearly" ? "year" : "month"
        }`
      : null;

    return (
      <div className="d-flex flex-column" style={{ gap: GAPS.md }}>
        <Text variant={TextVariant.sm} className="text-center">
          How many licenses do you want to add?
        </Text>
        <Stepper>
          <StepButton
            type="button"
            onClick={() => setCount((current) => Math.max(1, current - 1))}
            disabled={count <= 1 || isWorking}
          >
            −
          </StepButton>
          <SeatCount>{count}</SeatCount>
          <StepButton
            type="button"
            onClick={() => setCount((current) => Math.min(500, current + 1))}
            disabled={isWorking}
          >
            +
          </StepButton>
        </Stepper>
        <SummaryBox>
          <Text variant={TextVariant.sm}>
            Current plan: {currentPaid} license{currentPaid === 1 ? "" : "s"}
            {freeSeats > 0 ? ` + ${freeSeats} free admin seat${freeSeats === 1 ? "" : "s"}` : ""}
          </Text>
          {deltaLabel && (
            <Text
              variant={TextVariant.md}
              style={{ fontWeight: FONT_WEIGHTS.semibold }}
            >
              + {deltaLabel}
            </Text>
          )}
          <Text
            variant={TextVariant.sm}
            style={{
              color: `var(--color-foregroundSecondary, ${COLORS.foregroundSecondary})`,
            }}
          >
            Charged to your card on file, prorated for the current billing
            period.
          </Text>
        </SummaryBox>
      </div>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Add licenses"
      maxWidth={420}
      primaryButtonProps={{
        children: `Add ${count} license${count === 1 ? "" : "s"}`,
        variant: ButtonVariants.brand,
        onClick: handleConfirm,
        disabled: isWorking || !status || !billing?.subscriptionId,
        loading: isWorking,
      }}
      secondaryButtonProps={{
        children: "Cancel",
        variant: ButtonVariants.light,
        onClick: onClose,
      }}
    >
      {renderBody()}
    </Dialog>
  );
};

export default AddLicensesDialog;
